import React, { useEffect, useState } from 'react';

import routePaths from 'config/routes';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import { privateRoute } from 'services/privateRoute';
import { setBreadCrumbs, updateChallenges } from 'src/actions/appActions';
import { getBreadCrumbs, NotFound } from 'src/components';
import { builderChallenge } from 'src/components/challenge/builder/BuilderChallenge';
import BuildPageChallenge, {
  showMoreButton,
} from 'src/components/challenge/ChallengeBuildPage';
import ChallengeCard from 'src/components/challenge/ChallengeCard';
import { IRootReducers } from 'src/reducers';
import { ChallengedTabsEnum, PlatformEnums } from 'src/shared/enums';
import {
  ChallengeCardButton,
  ChallengeDetail,
  IChallenge,
} from 'src/shared/models';
import { appTypes } from 'src/types/appTypes';
import {
  fetchInitialData,
  getTranslatedData,
  renderChallengeCard,
} from 'utils';
import { ReduxPageContext } from 'src/store';

const { GET_EVENTS } = apiPaths.CHALLENGES;

interface OwnProps {
  children?: any;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

export const renderChallengeTalk = (
  challenge: ChallengeDetail
): JSX.Element => {
  const { formatMessage } = useIntl();
  const { challengeEvents } = challenge;
  const mainEvent = challengeEvents.find((e) => e.isMain);

  let config: ChallengeDetail;
  let userStatus: { idResource: number; status: number };
  let challengeCardButton: ChallengeCardButton = {
    title: formatMessage({ id: 'talk.see' }),
    path: routePaths.PAGES.TALK_DETAIL,
    className: 'challengeCard__link challengeCard__link--active',
  };

  const performSubscription =
    !mainEvent?.inscription.isPerformed && mainEvent?.inscription.isAvailable;
  const seeTheEvent = mainEvent?.status.isInProgress;

  switch (true) {
    case mainEvent?.inscription.isPerformed:
    case seeTheEvent:
      challengeCardButton = {
        ...challengeCardButton,
        title: formatMessage({ id: 'talk.see' }),
      };
      break;
    case performSubscription:
      challengeCardButton = {
        ...challengeCardButton,
        title: formatMessage({
          id: mainEvent?.status.isFull
            ? 'talk.inscribe.waiting-list'
            : 'talk.inscribe',
        }),
      };
      break;
  }

  config = {
    ...challenge,
    challengeName: getTranslatedData(challenge, 'name'),
    challengeDescription:
      getTranslatedData(challenge, 'description') ||
      getTranslatedData(challenge, 'descriptionLong'),
    challengeCardButton,
    userStatus,
  };

  return <ChallengeCard {...config} />;
};

const TalksPage: NextPage<Props> = ({
  eventsList,
  user,
  updateChallenges,
  setBreadCrumbs,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const [events, setEvents] = useState<ChallengeDetail[]>();
  const [filters, setFilters] = useState<ChallengedTabsEnum>(
    ChallengedTabsEnum.ALL
  );

  const [canShowMore, setCanShowMore] = useState<boolean>(
    eventsList.length >= 8
  );
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [requestPage, setRequestPage] = useState<number>(2); //Hemos pedido 8 y las páginas van de 4 en 4. Así que toca la pagina 2

  const handleShowMore = async () => {
    setIsLoadingMore(true);

    const request = `${GET_EVENTS}?page=${requestPage}&size=4`;

    try {
      const response = await getDataCall({
        dataPath: request,
        callConfig: {},
      });

      const ids = new Set(events.map((training) => training.challengeId));
      const newchallengeAcc: ChallengeDetail[] = [
        ...eventsList,
        ...response.data
          .filter((training: IChallenge) => !ids.has(training.idChallenge))
          .map((training: IChallenge) => builderChallenge(training, user)),
      ];

      updateChallenges({ events: newchallengeAcc });

      setRequestPage(requestPage + 1);
      setCanShowMore(response.data.length == 4);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderEvents = (): JSX.Element | JSX.Element[] => {
    let filteredEvents: ChallengeDetail[] = [];

    if (!events?.length) return <NotFound />;

    switch (filters) {
      case ChallengedTabsEnum.ATTENDED:
        events.forEach((_event) => {
          // TODO: use isCampaignCompleted and align
          const mainEvent = _event.challengeEvents.find((c) => c.isMain);
          if (
            mainEvent?.inscription.isPerformed &&
            mainEvent?.attend.isPerformed
          )
            filteredEvents.push(_event);
        });
        break;
      case ChallengedTabsEnum.FAVORITES:
        filteredEvents = events.filter((_event) => _event.challengeLikes.liked);
        break;
      case ChallengedTabsEnum.REGISTERED:
        events.forEach((_event) => {
          const mainEvent = _event.challengeEvents.find((c) => c.isMain);
          if (
            mainEvent?.inscription.isPerformed &&
            !mainEvent?.attend.isPerformed
          )
            filteredEvents.push(_event);
        });
        break;
      default:
        filteredEvents = events;
        break;
    }

    if (!filteredEvents.length) return <NotFound />;

    return filteredEvents.map((challenge: ChallengeDetail) =>
      renderChallengeCard({ challenge })
    );
  };

  useEffect(() => {
    setEvents(eventsList);

    const breadCrumbs = getBreadCrumbs(router, formatMessage);
    setTimeout(() => {
      setBreadCrumbs(breadCrumbs);
    }, 20);

    // clear breadcrumbs on unmount component
    return () => {
      // TODO: Remove setTimeout => check store.ts
      setTimeout(() => {
        setBreadCrumbs([]);
      }, 20);
    };
  }, [eventsList]);

  const showMoreComponent =
    filters == ChallengedTabsEnum.ALL
      ? showMoreButton(
          `page.marketing-materials.show-more`,
          isLoadingMore,
          canShowMore,
          handleShowMore
        )
      : null;

  return (
    <BuildPageChallenge
      pageClassName="talksPage"
      onChangeTab={setFilters}
      renderChallenges={renderEvents}
      showMoreComponent={showMoreComponent}
      filterComponent={null}
      tabs={[
        { key: ChallengedTabsEnum.ALL, label: 'app.all' },
        { key: ChallengedTabsEnum.REGISTERED, label: 'app.registered' },
        { key: ChallengedTabsEnum.ATTENDED, label: 'app.attended' },
        { key: ChallengedTabsEnum.FAVORITES, label: 'app.my-favourites' },
      ]}
    />
  );
};

/*-------------------------------------------*/
/*------------ GET INITIAL PROPS ------------*/
/*-------------------------------------------*/

const initialAction = async (
  accessToken: string,
  ctx: ReduxPageContext
): Promise<{ data: unknown }> => {
  try {
    const challenges = await fetchInitialData(
      accessToken,
      `${GET_EVENTS}?size=8`,
      ctx
    );

    const events: ChallengeDetail[] = challenges.map((challenge: IChallenge) =>
      builderChallenge(challenge, ctx.store.getState().auth.user)
    );

    if (events)
      ctx.store.dispatch({
        type: appTypes.APP_UPDATE_CHALLENGES,
        payload: { events },
      });

    return { data: events };
  } catch (error) {
    console.error('SERVER ERROR: ', error);
    return { data: null };
  }
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    eventsList: state.app.challenges.events,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ updateChallenges, setBreadCrumbs }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  privateRoute({
    WrappedComponent: TalksPage,
    initialAction,
    platforms: [PlatformEnums.PIN],
  })
);
